import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutTemplate, Plus } from "lucide-react";
import { ErrorBanner } from "../components/AuthShell.js";
import { Button, LinkButton } from "../components/ui/Button.js";
import { CARD_GRID_CLS, CardLink } from "../components/ui/Card.js";
import { EmptyState } from "../components/ui/EmptyState.js";
import { KindChip } from "../components/ui/KindChip.js";
import { PageHeader } from "../components/ui/PageHeader.js";
import { SearchInput } from "../components/ui/SearchInput.js";
import { SkeletonCardGrid } from "../components/ui/SkeletonCard.js";
import { useAuth } from "../context/AuthContext.js";
import { ApiError, fetchTemplates, type ApiTemplateSummary } from "../lib/api.js";

function TemplateCard({ tmpl }: { tmpl: ApiTemplateSummary }) {
  const composed = tmpl.sectionNames.filter(Boolean);
  return (
    <li>
      <CardLink to={`/templates/${tmpl.id}`}>
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-base font-medium text-ink">{tmpl.name}</h2>
          <KindChip kind={tmpl.kind} />
        </div>
        {composed.length > 0 ? (
          <p className="mt-2 line-clamp-2 text-sm text-muted">{composed.join(" · ")}</p>
        ) : null}
        <p className="mt-3 text-xs tabular-nums text-muted-sm">
          {tmpl.sectionCount} section{tmpl.sectionCount === 1 ? "" : "s"}
          {(tmpl.memberCount ?? 1) >= 2 ? ` · ${tmpl.memberCount} members` : ""}
        </p>
      </CardLink>
    </li>
  );
}

export function MyTemplatesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const mySub = user?.sub ?? "";
  const [templates, setTemplates] = useState<ApiTemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchTemplates();
        if (!cancelled) setTemplates(data);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof ApiError ? err.message : "Failed to load templates");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => t.name.toLowerCase().includes(q));
  }, [templates, filter]);

  const own = useMemo(() => filtered.filter((t) => t.ownerId === mySub), [filtered, mySub]);
  const shared = useMemo(() => filtered.filter((t) => t.ownerId !== mySub), [filtered, mySub]);
  const hasAnyShared = useMemo(() => templates.some((t) => t.ownerId !== mySub), [templates, mySub]);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:py-10">
      <PageHeader
        title="My Templates"
        subtitle="Blueprints. Combine lists into a plan you reuse every time."
        actions={
          <Button
            type="button"
            icon={<Plus size={16} aria-hidden="true" />}
            onClick={() => navigate("/templates/new")}
          >
            New template
          </Button>
        }
      />

      <ErrorBanner message={error} />

      {!loading && templates.length > 0 ? (
        <div className="mt-4">
          <SearchInput
            id="templateFilter"
            aria-label="Search templates"
            placeholder="Filter by name…"
            value={filter}
            onChange={setFilter}
          />
        </div>
      ) : null}

      {loading ? (
        <SkeletonCardGrid />
      ) : templates.length === 0 ? (
        <EmptyState
          icon={<LayoutTemplate size={28} className="text-muted" aria-hidden="true" />}
          title="No templates yet"
          body="Templates are blueprints. When you find yourself making the same checklist twice, save one as a template and create it in one click next time."
          primary={
            <Button type="button" onClick={() => navigate("/templates/new")}>
              New template
            </Button>
          }
          secondary={<LinkButton variant="secondary" to="/checklists">Start from a checklist instead</LinkButton>}
        />
      ) : filtered.length === 0 ? (
        <p className="mt-8 text-sm text-muted">No templates match &ldquo;{filter.trim()}&rdquo;.</p>
      ) : (
        <div className="mt-6 space-y-8">
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
              Owned by me
            </h2>
            {own.length === 0 ? (
              <p className="mt-3 text-sm text-muted">No owned templates in this filter.</p>
            ) : (
              <ul className={`mt-3 ${CARD_GRID_CLS}`}>
                {own.map((tmpl) => (
                  <TemplateCard key={tmpl.id} tmpl={tmpl} />
                ))}
              </ul>
            )}
          </section>

          {hasAnyShared ? (
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
                Shared with me
              </h2>
              {shared.length === 0 ? (
                <p className="mt-3 text-sm text-muted">
                  No shared templates match &ldquo;{filter.trim()}&rdquo;.
                </p>
              ) : (
                <ul className={`mt-3 ${CARD_GRID_CLS}`}>
                  {shared.map((tmpl) => (
                    <TemplateCard key={tmpl.id} tmpl={tmpl} />
                  ))}
                </ul>
              )}
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}
